/**
 * Route guards that read the server's navigation decision (f9.2).
 *
 * **This is not access control**, exactly as `RequireActiveAccount` is not.
 * It reads `canSee` from `SessionScopeContext` and reimplements no rule
 * (adr-14): a deep link past it reaches a screen whose every query the server
 * refuses or narrows on its own authority, and audits the refusal. It exists
 * so a caller who types an address the server did not offer them sees an
 * explanation instead of a screen that fills with nothing.
 *
 * It RENDERS a state and never redirects: a redirect discards the address the
 * user entered and can loop against the computed landing.
 */

import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { landingPathFor } from '@/navigation/landing';
import { useSessionScope } from '@/session/SessionScopeContext';

import { LimitedAccess, NoAccessYet, NotAvailableToYou } from './AccessStates';

export function RequirePermittedNavigation({
  navKey,
  children,
}: {
  navKey: string;
  children: ReactNode;
}) {
  const { canSee } = useSessionScope();
  if (!canSee(navKey)) return <NotAvailableToYou />;
  return <>{children}</>;
}

/**
 * `/`: the first permitted entry, or -- when the server permitted none -- the
 * landing state that tells the truth about why.
 *
 * `has_any_scope` chooses between two pieces of COPY, never whether anything
 * is shown: with an empty `navigation` there is no entry and no route either
 * way. "You are named nowhere" would be false for a caller named on
 * engagements, and the audit log could disprove it.
 */
export function LandingRoute() {
  const { session } = useSessionScope();
  const landing = landingPathFor(session?.navigation ?? []);
  if (landing) return <Navigate to={landing} replace />;
  return session?.scope?.has_any_scope ? <LimitedAccess /> : <NoAccessYet />;
}
